import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  Modal,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  ReloadOutlined,
  RobotOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import { Skill } from '../models/job.model';
import {
  JobSkillsSuggestionResponse,
  SuggestedJobSkill,
} from '../models/job-skills-suggestion.model';
import { JobSkillsSuggestionService } from '../services/job-skills-suggestion.service';
import { UtilService } from '../../../../../core/services/util.service';

const LEVEL_OPTIONS = ['beginner', 'intermediate', 'advanced', 'expert'] as const;

type SkillLevel = (typeof LEVEL_OPTIONS)[number];
type SkillKind = 'required' | 'niceToHave';

interface SelectableSkill extends SuggestedJobSkill {
  key: string;
  kind: SkillKind;
  selected: boolean;
  level: SkillLevel;
}

interface JobSkillSuggestionsModalProps {
  open: boolean;
  onClose: () => void;
  description: string;
  jobTitle?: string;
  category?: string;
  jobId?: string;
  existingSkills: Skill[];
  onApply: (skills: Skill[]) => void;
}

const LEVEL_COLORS: Record<SkillLevel, string> = {
  beginner: 'green',
  intermediate: 'cyan',
  advanced: 'blue',
  expert: 'purple',
};

const LEVEL_LABELS: Record<SkillLevel, string> = {
  beginner: 'Beginner',
  intermediate: 'Intermediate',
  advanced: 'Advanced',
  expert: 'Expert',
};

function toLevel(value?: string): SkillLevel {
  const normalized = (value ?? 'intermediate').toLowerCase();
  return LEVEL_OPTIONS.includes(normalized as SkillLevel)
    ? (normalized as SkillLevel)
    : 'intermediate';
}

function buildRows(response: JobSkillsSuggestionResponse): SelectableSkill[] {
  const required = (response.requiredSkills ?? []).map((item, index) => ({
    ...item,
    key: `required-${item.skill}-${index}`,
    kind: 'required' as const,
    selected: true,
    level: toLevel(item.suggestedLevel),
  }));
  const nice = (response.niceToHaveSkills ?? []).map((item, index) => ({
    ...item,
    key: `nice-${item.skill}-${index}`,
    kind: 'niceToHave' as const,
    selected: true,
    level: toLevel(item.suggestedLevel),
  }));
  return [...required, ...nice];
}

function resetModalState(
  setLoaded: (value: boolean) => void,
  setRows: (value: SelectableSkill[]) => void,
  setFromCache: (value: boolean) => void,
  setErrorMessage: (value: string | null) => void,
  sessionCacheRef: React.MutableRefObject<{
    description: string;
    rows: SelectableSkill[];
    fromCache: boolean;
  } | null>
) {
  setLoaded(false);
  setRows([]);
  setFromCache(false);
  setErrorMessage(null);
  sessionCacheRef.current = null;
}

interface SkillSectionProps {
  title: string;
  description: string;
  accentClass: string;
  rows: SelectableSkill[];
  onToggleRow: (key: string, selected: boolean) => void;
  onToggleSection: (kind: SkillKind, selected: boolean) => void;
  onLevelChange: (key: string, level: SkillLevel) => void;
}

function SkillSection({
  title,
  description,
  accentClass,
  rows,
  onToggleRow,
  onToggleSection,
  onLevelChange,
}: SkillSectionProps) {
  const kind = rows[0]?.kind ?? 'required';
  const selectedInSection = rows.filter((row) => row.selected).length;
  const allSelected = rows.length > 0 && selectedInSection === rows.length;
  const someSelected = selectedInSection > 0 && !allSelected;

  const columns: ColumnsType<SelectableSkill> = [
    {
      title: (
        <Checkbox
          checked={allSelected}
          indeterminate={someSelected}
          aria-label={`Select all ${title.toLowerCase()} skills`}
          onChange={(event) => onToggleSection(kind, event.target.checked)}
        />
      ),
      dataIndex: 'selected',
      width: 48,
      render: (_, record) => (
        <Checkbox
          checked={record.selected}
          aria-label={`Include ${record.skill}`}
          onChange={(event) => onToggleRow(record.key, event.target.checked)}
        />
      ),
    },
    {
      title: 'Skill',
      dataIndex: 'skill',
      ellipsis: true,
    },
    {
      title: 'Proficiency',
      dataIndex: 'level',
      width: 150,
      render: (level: SkillLevel, record) => (
        <Select
          size="small"
          className="w-full min-w-[130px]"
          value={level}
          aria-label={`Proficiency for ${record.skill}`}
          options={LEVEL_OPTIONS.map((option) => ({
            label: LEVEL_LABELS[option],
            value: option,
          }))}
          onChange={(value: SkillLevel) => onLevelChange(record.key, value)}
        />
      ),
    },
  ];

  if (rows.length === 0) {
    return null;
  }

  return (
    <section aria-labelledby={`skill-section-${kind}`} className="space-y-2">
      <div className={`rounded-lg border px-3 py-2 ${accentClass}`}>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <Typography.Text
              id={`skill-section-${kind}`}
              strong
              className="text-sm text-gray-800"
            >
              {title}
            </Typography.Text>
            <Typography.Paragraph type="secondary" className="!mb-0 text-xs">
              {description}
            </Typography.Paragraph>
          </div>
          <Tag className="m-0">
            {selectedInSection} of {rows.length} selected
          </Tag>
        </div>
      </div>
      <Table
        size="small"
        pagination={false}
        columns={columns}
        dataSource={rows}
        rowKey="key"
        className="skill-suggestions-table"
      />
    </section>
  );
}

export function JobSkillSuggestionsModal({
  open,
  onClose,
  description,
  jobTitle,
  category,
  jobId,
  existingSkills,
  onApply,
}: JobSkillSuggestionsModalProps) {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<SelectableSkill[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [fromCache, setFromCache] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const sessionCacheRef = useRef<{
    description: string;
    rows: SelectableSkill[];
    fromCache: boolean;
  } | null>(null);

  const existingSkillNames = useMemo(
    () => existingSkills.map((skill) => skill.skillName),
    [existingSkills]
  );

  const plainDescriptionEmpty = useMemo(
    () =>
      !description ||
      description === '<p><br></p>' ||
      description === '<p></p>' ||
      description.replace(/<[^>]+>/g, '').trim() === '',
    [description]
  );

  const requiredRows = useMemo(
    () => rows.filter((row) => row.kind === 'required'),
    [rows]
  );
  const niceToHaveRows = useMemo(
    () => rows.filter((row) => row.kind === 'niceToHave'),
    [rows]
  );

  const selectedCount = useMemo(
    () => rows.filter((row) => row.selected).length,
    [rows]
  );

  const fetchSuggestions = useCallback(
    async (refresh = false) => {
      if (plainDescriptionEmpty) {
        UtilService.Alert(
          'Description required',
          'warning',
          'Add a job description before requesting skill suggestions.'
        );
        return;
      }

      if (
        !refresh &&
        sessionCacheRef.current?.description === description &&
        sessionCacheRef.current.rows.length > 0
      ) {
        setRows(sessionCacheRef.current.rows);
        setFromCache(sessionCacheRef.current.fromCache);
        setLoaded(true);
        setErrorMessage(null);
        return;
      }

      setLoading(true);
      setErrorMessage(null);
      try {
        const response = await JobSkillsSuggestionService.Suggest({
          description,
          jobTitle,
          category,
          jobId,
          refresh,
          existingSkills: existingSkillNames,
        });
        const nextRows = buildRows(response);
        setRows(nextRows);
        setLoaded(true);
        setFromCache(Boolean(response.fromCache));
        sessionCacheRef.current = {
          description,
          rows: nextRows,
          fromCache: Boolean(response.fromCache),
        };
      } catch (error) {
        console.error('Skill suggestion failed:', error);
        setLoaded(true);
        setRows([]);
        setErrorMessage(
          'Could not generate skill suggestions. Check that AI research is enabled on the server, then try Refresh.'
        );
      } finally {
        setLoading(false);
      }
    },
    [
      plainDescriptionEmpty,
      description,
      jobTitle,
      category,
      jobId,
      existingSkillNames,
    ]
  );

  const handleOpen = useCallback(() => {
    void fetchSuggestions(false);
  }, [fetchSuggestions]);

  useEffect(() => {
    if (
      sessionCacheRef.current !== null &&
      sessionCacheRef.current.description !== description
    ) {
      sessionCacheRef.current = null;
      setLoaded(false);
      setRows([]);
      setFromCache(false);
      setErrorMessage(null);
    }
  }, [description]);

  const updateRow = useCallback(
    (key: string, patch: Partial<Pick<SelectableSkill, 'selected' | 'level'>>) => {
      setRows((prev) => {
        const next = prev.map((row) =>
          row.key === key ? { ...row, ...patch } : row
        );
        if (sessionCacheRef.current?.description === description) {
          sessionCacheRef.current = { ...sessionCacheRef.current, rows: next };
        }
        return next;
      });
    },
    [description]
  );

  const toggleSection = useCallback(
    (kind: SkillKind, selected: boolean) => {
      setRows((prev) => {
        const next = prev.map((row) =>
          row.kind === kind ? { ...row, selected } : row
        );
        if (sessionCacheRef.current?.description === description) {
          sessionCacheRef.current = { ...sessionCacheRef.current, rows: next };
        }
        return next;
      });
    },
    [description]
  );

  const toggleAll = useCallback(
    (selected: boolean) => {
      setRows((prev) => {
        const next = prev.map((row) => ({ ...row, selected }));
        if (sessionCacheRef.current?.description === description) {
          sessionCacheRef.current = { ...sessionCacheRef.current, rows: next };
        }
        return next;
      });
    },
    [description]
  );

  const handleClose = () => {
    onClose();
    resetModalState(setLoaded, setRows, setFromCache, setErrorMessage, sessionCacheRef);
  };

  const handleApply = () => {
    const selected = rows.filter((row) => row.selected);
    const merged = [...existingSkills];
    for (const row of selected) {
      const duplicate = merged.some(
        (skill) =>
          skill.skillName.toLowerCase() === row.skill.toLowerCase() &&
          skill.skillLevel === row.level
      );
      if (!duplicate) {
        merged.push({
          id: '',
          skillName: row.skill,
          skillLevel: row.level,
        });
      }
    }
    onApply(merged);
    handleClose();
  };

  const allSelected = rows.length > 0 && selectedCount === rows.length;
  const someSelected = selectedCount > 0 && !allSelected;

  return (
    <Modal
      title={
        <Space align="start" size={12}>
          <span
            className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-600"
            aria-hidden
          >
            <RobotOutlined />
          </span>
          <div>
            <Typography.Title level={5} className="!mb-0">
              Suggested skills from description
            </Typography.Title>
            <Typography.Text type="secondary" className="text-sm">
              Pick skills to add and set proficiency before saving the job.
            </Typography.Text>
          </div>
        </Space>
      }
      open={open}
      onCancel={handleClose}
      afterOpenChange={(visible) => {
        if (visible) {
          handleOpen();
        }
      }}
      width={760}
      destroyOnHidden
      footer={
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Button
            icon={<ReloadOutlined />}
            onClick={() => void fetchSuggestions(true)}
            disabled={loading}
          >
            Refresh
          </Button>
          <Space>
            <Button onClick={handleClose}>Cancel</Button>
            <Button
              type="primary"
              icon={<CheckCircleOutlined />}
              disabled={selectedCount === 0 || loading}
              onClick={handleApply}
            >
              Add selected ({selectedCount})
            </Button>
          </Space>
        </div>
      }
    >
      <div className="space-y-4">
        {fromCache && loaded && !loading ? (
          <Alert
            type="info"
            showIcon
            message="Using saved suggestions"
            description="The description has not changed since the last analysis. Use Refresh to regenerate."
            className="!mb-0"
          />
        ) : null}

        {errorMessage ? (
          <Alert
            type="error"
            showIcon
            message="Suggestions unavailable"
            description={errorMessage}
            action={
              <Button size="small" onClick={() => void fetchSuggestions(true)}>
                Retry
              </Button>
            }
          />
        ) : null}

        {loading ? (
          <div
            className="flex min-h-[220px] flex-col items-center justify-center rounded-lg border border-dashed border-gray-200 bg-gray-50/80 py-12"
            role="status"
            aria-live="polite"
            aria-busy="true"
          >
            <Spin size="large" />
            <Typography.Text type="secondary" className="mt-4">
              Analyzing job description…
            </Typography.Text>
          </div>
        ) : loaded && rows.length === 0 && !errorMessage ? (
          <Alert
            type="info"
            showIcon
            message="No new skills suggested"
            description="Try expanding the description with technologies and responsibilities, or add skills manually."
          />
        ) : rows.length > 0 ? (
          <>
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-gray-50 px-3 py-2">
              <Space size="middle" wrap>
                <Typography.Text className="text-sm text-gray-700">
                  <strong>{rows.length}</strong> suggested
                </Typography.Text>
                <Typography.Text className="text-sm text-gray-500">·</Typography.Text>
                <Typography.Text className="text-sm text-gray-700">
                  <strong>{selectedCount}</strong> selected
                </Typography.Text>
                {requiredRows.length > 0 ? (
                  <>
                    <Typography.Text className="text-sm text-gray-500">·</Typography.Text>
                    <Tag color="red" className="m-0">
                      {requiredRows.length} required
                    </Tag>
                  </>
                ) : null}
                {niceToHaveRows.length > 0 ? (
                  <Tag className="m-0">{niceToHaveRows.length} nice to have</Tag>
                ) : null}
              </Space>
              <Space size="small">
                <Checkbox
                  checked={allSelected}
                  indeterminate={someSelected}
                  aria-label="Select all suggested skills"
                  onChange={(event) => toggleAll(event.target.checked)}
                >
                  Select all
                </Checkbox>
              </Space>
            </div>

            <div className="max-h-[min(52vh,420px)] space-y-5 overflow-y-auto pr-1">
              <SkillSection
                title="Required skills"
                description="Core competencies expected for this role."
                accentClass="border-red-100 bg-red-50/60"
                rows={requiredRows}
                onToggleRow={(key, selected) => updateRow(key, { selected })}
                onToggleSection={toggleSection}
                onLevelChange={(key, level) => updateRow(key, { level })}
              />
              <SkillSection
                title="Nice to have"
                description="Helpful extras that strengthen a candidate profile."
                accentClass="border-gray-200 bg-gray-50"
                rows={niceToHaveRows}
                onToggleRow={(key, selected) => updateRow(key, { selected })}
                onToggleSection={toggleSection}
                onLevelChange={(key, level) => updateRow(key, { level })}
              />
            </div>

            <Typography.Paragraph type="secondary" className="!mb-0 text-xs">
              Proficiency colors match the skills list:{' '}
              {LEVEL_OPTIONS.map((level) => (
                <Tag key={level} color={LEVEL_COLORS[level]} className="mx-0.5">
                  {LEVEL_LABELS[level]}
                </Tag>
              ))}
            </Typography.Paragraph>
          </>
        ) : null}
      </div>
    </Modal>
  );
}

interface JobSkillSuggestionsTriggerProps {
  description: string;
  jobTitle?: string;
  category?: string;
  jobId?: string;
  existingSkills: Skill[];
  setSkills: React.Dispatch<React.SetStateAction<Skill[]>>;
  disabled?: boolean;
}

export function JobSkillSuggestionsTrigger({
  description,
  jobTitle,
  category,
  jobId,
  existingSkills,
  setSkills,
  disabled,
}: JobSkillSuggestionsTriggerProps) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button
        type="default"
        icon={<RobotOutlined />}
        disabled={disabled}
        onClick={() => setOpen(true)}
        className="text-blue-600 border-blue-300"
      >
        Suggest from description
      </Button>
      <JobSkillSuggestionsModal
        open={open}
        onClose={() => setOpen(false)}
        description={description}
        jobTitle={jobTitle}
        category={category}
        jobId={jobId}
        existingSkills={existingSkills}
        onApply={(skills) => setSkills(skills)}
      />
    </>
  );
}

export default JobSkillSuggestionsTrigger;
